import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import {
    IFilterMenu,
    IItemsListFilter,
    ISelectedFilterItem,
    ISharingMenuData,
} from '@fi-shared/fi-items-list/fi-items-list-filter/fi-items-list-filter/fi-items-list-filter';
import { AppTextService } from '@fi-shared/app-text';
import { IAccessRightFilterCollection, ILightWeightObject, ILightWeightUser } from '@fi-shared/models';
import { MatMenu, MatMenuTrigger } from '@angular/material/menu';
import { FiFilterType } from '../../../fi-filter-type-enum';
import { ISideListOptionsItem } from '@fi-shared/fi-items-list/fi-items-list';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { fiDoesLightWeightObjectHaveChildren } from '@fi-shared/utils/fi-does-light-weight-object-have-children';
import { FiItemsListFilterHelperService } from '@fi-shared/fi-items-list/fi-items-list-filter/fi-items-list-filter-helper.service';
import { FiFilterSavedFiltersMenuComponent } from '@fi-shared/fi-items-list/fi-items-list-filter/fi-items-list-filter/fi-filter-saved-filters-menu/fi-filter-saved-filters-menu.component';
import {
    fiGetSelectedAreaIdsForFilter,
    getSelectedItemIdsFoldableData,
} from '@fi-shared/utils/fi-get-selected-area-ids-for-filter';
import { isDefined } from '@fi-shared/utils';

@Component({
    selector: 'fi-filter-settings-menus',
    templateUrl: './fi-filter-settings-menus.component.html',
    styleUrls: ['./fi-filter-settings-menus.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-filter-settings-menus' },
    standalone: false,
})
export class FiFilterSettingsMenusComponent implements OnInit, OnChanges, OnDestroy {
    @Input() accessRightFilterCollection: IAccessRightFilterCollection;
    @Input() currentFilter: IItemsListFilter;
    @Output() currentFilterChange = new EventEmitter<IItemsListFilter>();
    @Input() selectedFilterItems: ISelectedFilterItem[] = [];
    @Output() toggleFilterSettingsDataItem = new EventEmitter<{ itemId: number; filterType: FiFilterType }>();
    @Output() onFilterSettingsChanged = new EventEmitter<void>();

    @Input() categoryItems: ISideListOptionsItem[];
    @Input() changedItems: ISideListOptionsItem[];
    @Input() tagItems: ISideListOptionsItem[];
    @Input() savedFilters: IItemsListFilter[] = [];
    @Input() accessRightFilterChange$: Observable<FiFilterType>;

    @Output() onOpenRenameSavedFilterDialog = new EventEmitter<IItemsListFilter>();
    @Output() onOpenChangeSavedFilterDialog = new EventEmitter<IItemsListFilter>();
    @Output() onOpenDeleteSavedFilterDialog = new EventEmitter<IItemsListFilter>();
    @Output() onSelectSavedFilter = new EventEmitter<IItemsListFilter>();

    filterMenus: IFilterMenu[] = [];
    shareWithMenuData: ISharingMenuData<ILightWeightObject | ILightWeightUser>[] = [];
    FiFilterType = FiFilterType;

    @ViewChild('categoryMenu', { static: true }) categoryMatMenu: MatMenu;
    @ViewChild('changedMenu', { static: true }) changedMatMenu: MatMenu;
    @ViewChild('taggedWithMenu', { static: true }) taggedWithMatMenu: MatMenu;
    @ViewChild('sharedWithMenu', { static: true }) sharedWithMatMenu: MatMenu;
    @ViewChild('fiFilterSavedFiltersMenuComponent', { static: true })
    fiFilterSavedFiltersMenuComponentRef: FiFilterSavedFiltersMenuComponent<IItemsListFilter>;

    @ViewChild(MatMenuTrigger) trigger: MatMenuTrigger;

    optionDisabledFn = fiDoesLightWeightObjectHaveChildren;

    constructor(
        private appTextService: AppTextService,
        private cdr: ChangeDetectorRef,
        private fiItemsListFilterHelperService: FiItemsListFilterHelperService,
    ) {}

    ngOnInit() {
        this.setFilterMenus();
        this.setSelectedTagItems();
        this.updateSharedWithSelectedItemsOnAccessRightFilterChange();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.selectedFilterItems) {
            this.setSelectedTagItems();
        }
    }

    private updateSharedWithSelectedItemsOnAccessRightFilterChange() {
        this.accessRightFilterChange$.pipe(takeUntil(this.ngOnDestroy$)).subscribe((filterType) => {
            this.setSelectedTagItems();
            if (filterType === undefined) {
                this.initSharedWithMenuData();
                return;
            }

            const changedShareWithMenuData = this.shareWithMenuData.find(
                (menuData) => menuData.filterType === filterType,
            );

            switch (filterType) {
                case FiFilterType.User:
                    changedShareWithMenuData.selectedItemIds = this.currentFilter.Settings.UserIds;
                    break;
                case FiFilterType.BusinessUnit:
                    changedShareWithMenuData.selectedItemIds = this.currentFilter.Settings.BusinessUnitIds;
                    break;
                /* case FiFilterType.Cluster:
                    changedShareWithMenuData.selectedItems = this.currentFilter.Settings.ClusterIds;
                    break; */
                case FiFilterType.Area:
                    {
                        const selectedItemIdsData = getSelectedItemIdsFoldableData(
                            this.currentFilter.Settings.AreaIds,
                            <any>changedShareWithMenuData.menuItems,
                        );
                        changedShareWithMenuData.selectedItemIds = selectedItemIdsData.selectedItemIds;
                        changedShareWithMenuData.selectedChildItemIds = selectedItemIdsData.selectedChildItemIds;
                    }
                    break;
                case FiFilterType.BusinessArea:
                    changedShareWithMenuData.selectedItemIds = this.currentFilter.Settings.BusinessAreaIds;
                    break;
            }
        });
    }

    closeMatMenu() {
        this.trigger.closeMenu();
    }

    setChangedOnFilterType(changeItem: ISideListOptionsItem) {
        this.currentFilter.Settings.ChangedOnRange = undefined;
        this.currentFilter.Settings.ChangedOnFilterType = changeItem.value;
        this.onFilterSettingsChanged.emit();
    }

    onSelectedDateRangeChange() {
        this.currentFilter.Settings.ChangedOnFilterType = undefined;
        this.onFilterSettingsChanged.emit();
    }

    isSideListOptionSelected(sideListOptionValue: number, filterType: FiFilterType): boolean {
        return !!this.selectedFilterItems.find((filterItem) => {
            return this.fiItemsListFilterHelperService.isFilterItemSelected(
                { value: sideListOptionValue, filterType },
                filterItem,
            );
        });
    }

    selectedTagItems$ = new BehaviorSubject<ISideListOptionsItem[]>([]);
    tagOptionDisabledFn = (item: ISideListOptionsItem, array: ISideListOptionsItem[]) => item.IsDisabled; // user has no access to tag

    setSelectedTagItems() {
        const selectedTagFilterItemIds: number[] = this.selectedFilterItems
            .filter((filterItem) => filterItem.filterType === FiFilterType.Tag)
            .map((filterItem) => filterItem.value as number);

        this.tagItems = this.tagItems.map((tag) => {
            if (!isDefined(tag.ParentId)) delete tag.ParentId;
            return tag;
        });

        const selectedTagItems: ISideListOptionsItem[] = this.tagItems.filter((tagItem) =>
            selectedTagFilterItemIds.includes(tagItem.value),
        );
        this.selectedTagItems$.next(selectedTagItems);
    }

    filterItemToggle($event: MouseEvent, item: ISideListOptionsItem, filterType: FiFilterType) {
        $event?.stopPropagation();

        this.toggleFilterSettingsDataItem.emit({ itemId: item.value as number, filterType });
        this.onFilterSettingsChanged.emit();
        this.setSelectedTagItems();
    }

    sharingWithItemChange(menuData: ISharingMenuData<ILightWeightObject | ILightWeightUser>) {
        this.setSharingWidthFilterData(menuData);
        this.currentFilterChange.emit(this.currentFilter);
        this.onFilterSettingsChanged.emit();
    }

    private setSharingWidthFilterData(menuData: ISharingMenuData<ILightWeightObject | ILightWeightUser>) {
        switch (menuData.filterType) {
            case FiFilterType.User:
                this.currentFilter.Settings.UserIds = menuData.selectedItemIds;
                break;
            case FiFilterType.BusinessUnit:
                this.currentFilter.Settings.BusinessUnitIds = menuData.selectedItemIds;
                break;
            /* case FiFilterType.Cluster:
                this.currentFilter.Settings.ClusterIds = menuData.selectedItems;
                break; */
            case FiFilterType.Area:
                this.currentFilter.Settings.AreaIds = [...menuData.selectedItemIds, ...menuData.selectedChildItemIds];
                break;
            case FiFilterType.BusinessArea:
                this.currentFilter.Settings.BusinessAreaIds = menuData.selectedItemIds;
                break;
        }
    }

    private setFilterMenus() {
        // this method might be changed later when we add this new filter feature for other lists,
        // so some of them might won't have all this filters

        this.filterMenus = [
            {
                label: this.appTextService.get('common.category'),
                matMenu: this.categoryMatMenu,
            },
            {
                label: this.appTextService.get('common.changed'),
                matMenu: this.changedMatMenu,
            },
            {
                label: this.appTextService.get('common.tagged_with'),
                matMenu: this.taggedWithMatMenu,
            },
            {
                label: this.appTextService.get('common.shared_with'),
                matMenu: this.sharedWithMatMenu,
            },
            {
                label: this.appTextService.get('common.saved_filters'),
                matMenu: this.fiFilterSavedFiltersMenuComponentRef.savedFiltersMatMenu,
            },
        ];

        this.initSharedWithMenuData();
        this.cdr.markForCheck();
    }

    private initSharedWithMenuData() {
        // const availableAreas: ILightWeightArea[] =
        this.shareWithMenuData = [
            {
                menuItems: this.accessRightFilterCollection.AccessRightUserFilter.Users,
                selectedItemIds: this.currentFilter.Settings.UserIds,
                label: this.appTextService.get('user.user'),
                displayFieldPropertyName: 'FullName',
                isProfileIconShown: true,
                filterType: FiFilterType.User,
            },
            {
                menuItems: this.accessRightFilterCollection.AccessRightCoreFilter.BusinessUnits,
                selectedItemIds: this.currentFilter.Settings.BusinessUnitIds,
                label: this.appTextService.get('common.unit'),
                displayFieldPropertyName: 'Name',
                isProfileIconShown: true,
                profileIconName: 'homeCcmIcon',
                filterType: FiFilterType.BusinessUnit,
            },
            {
                menuItems: this.accessRightFilterCollection.AccessRightCoreFilter.Areas,
                selectedItemIds: fiGetSelectedAreaIdsForFilter(
                    this.accessRightFilterCollection.AccessRightCoreFilter.Areas,
                    this.currentFilter.Settings.AreaIds,
                ),
                label: this.appTextService.get('common.areas'),
                displayFieldPropertyName: 'Name',
                filterType: FiFilterType.Area,
                childrenFilterType: FiFilterType.Area,
                selectedChildItemIds: fiGetSelectedAreaIdsForFilter(
                    this.accessRightFilterCollection.AccessRightCoreFilter.Areas,
                    this.currentFilter.Settings.AreaIds,
                    true,
                ),
                isSubmenuLayout: true,
            },
            /*{
            menuItems: this.accessRightFilterCollection.AccessRightCoreFilter.Clusters,
            selectedItems: this.currentFilter.Settings.ClusterIds,
            label: this.appTextService.get('common.clusters'),
            displayFieldPropertyName: 'Name',
            filterType: FiFilterType.Cluster
        }, */ {
                menuItems: this.accessRightFilterCollection.AccessRightCoreFilter.BusinessAreas,
                selectedItemIds: this.currentFilter.Settings.BusinessAreaIds,
                label: this.appTextService.get('common.business_areas'),
                displayFieldPropertyName: 'Name',
                filterType: FiFilterType.BusinessArea,
            },
        ];
    }

    selectFilter(savedFilter: IItemsListFilter) {
        this.closeMatMenu();
        this.onSelectSavedFilter.emit(savedFilter);
    }

    openRenameSavedFilterDialog(savedFilterData: IItemsListFilter) {
        this.onOpenRenameSavedFilterDialog.emit(savedFilterData);
    }

    openChangeSavedFilterDialog(savedFilterData: IItemsListFilter) {
        this.onOpenChangeSavedFilterDialog.emit(savedFilterData);
    }

    openDeleteSavedFilterDialog(savedFilterData: IItemsListFilter) {
        this.onOpenDeleteSavedFilterDialog.emit(savedFilterData);
    }

    isFoldableSelect(menuData: ISharingMenuData<ILightWeightObject | ILightWeightUser>) {
        return menuData.isSubmenuLayout && menuData.filterType === FiFilterType.Area;
    }

    isFoldableSelectMainMenuCloseButtonShown(
        menuData: ISharingMenuData<ILightWeightObject | ILightWeightUser>,
    ): boolean {
        return menuData.menuItems.every((menuItem: ILightWeightObject | ILightWeightUser) => {
            return 'ChildItems' in menuItem && menuItem.ChildItems instanceof Array && !!menuItem.ChildItems.length;
        });
    }

    protected ngOnDestroy$ = new Subject<void>();
    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
